import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send } from 'lucide-react';

interface NamePromptProps {
  onSubmit: (name: string) => Promise<void>;
  isLoading?: boolean;
}

export function NamePrompt({ onSubmit, isLoading }: NamePromptProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter your name');
      return;
    }

    if (trimmed.length > 50) {
      setError('Name must be 50 characters or less');
      return;
    }

    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-primary/5">
      <div className="w-full max-w-sm space-y-8 animate-in">
        {/* Logo/Title */}
        <div className="text-center space-y-2">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center glow">
            <span className="text-4xl">📡</span>
          </div>
          <h1 className="text-3xl font-display font-bold gradient-text">
            Ping
          </h1>
          <p className="text-muted-foreground">
            Share moments with the display
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              className="h-14 text-lg text-center"
              autoFocus
            />
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
          </div>

          <Button
            type="submit"
            size="xl"
            className="w-full"
            disabled={isLoading || !name.trim()}
          >
            {isLoading ? (
              <span className="animate-pulse">Connecting...</span>
            ) : (
              <>
                Continue
                <Send className="w-5 h-5" />
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-center text-muted-foreground">
          Your name will be shown with your posts
        </p>
      </div>
    </div>
  );
}

